import { getCoreDieFaceValues } from './coreAttackStyles';
import {
  getBossMoment,
  getCoreCounterplay,
  getCoreThreat,
} from './bossTactics';
import { rollCoreDamage, CORE_MASSIVE_ATTACK_BONUS } from './coreDamage';
import CoreAttackDiePreview from './CoreAttackDiePreview';
import CoreboundIcon from './CoreboundIcon';
import { getGuardianAttackTiming } from './guardianCamera';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GuardianDamageCalculation from './GuardianDamageCalculation';
import './CoreBoardCinematic.css';
import RelicActivation from './RelicActivation';
import HealingFeedback from './HealingFeedback';
import RelicIcon from './RelicIcon';
import { RELICS } from './relics';
import CoreFinalBlow from './CoreFinalBlow';
import CoreHealthFill, { CoreShieldValue } from './CoreHealthFill';
import CorePhaseSpell from './CorePhaseSpell';
import { completeCorePhaseSpell } from './coreBoardPhases';
import { getCoreAppearance } from './coreAppearance';
import { getCoreDieAppearance } from './coreDieAppearance';
import CoreCounterattack from './CoreCounterattack';
import { getCoreLaserDuration, getCoreLaserOutcome } from './coreLaser';
import GlitchPrismScene from './GlitchPrismScene';
import SlingshotLauncher from './SlingshotLauncher';
import { formatDieValue, rollDie, selectDie } from './dice';
import RelicAbilityBar from './RelicAbilityBar';
import { recordThrowEvent } from './throwCombo';
import {
  getBoardEffectTotal,
  getBoardRollSteps,
  getChainReactionState,
  toggleChainReaction,
  getTriggeredBoardSquares,
  hasChainReaction,
} from './chainReaction';
import { activatePortalPair, getPortalPairState } from './portalPair';
import {
  getDecoyBeaconState,
  startDecoyBeaconThrow,
  toggleDecoyBeacon,
} from './decoyBeacon';
import {
  activateGhostBump,
  getGhostBumpState,
  startGhostBumpThrow,
} from './ghostBump';
import {
  activatePhaseAnchor,
  getPhaseAnchorState,
  PHASE_ANCHOR_DURATION_MS,
} from './phaseAnchor';
import {
  getSlingshotFallbackTile,
  normalizeSlingshotLaunch,
} from './slingshot';
import { BOARD_BUMP_SETTLE_TIME, MAX_BOARD_BUMPS } from './boardBump';
import BoardTreasureReveal from './BoardTreasureReveal';
import { TREASURE_TILE_CHARGE_MS } from './treasureReveal';
import MirrorChipActivation from './MirrorChipActivation';
import BoardTileChoice from './BoardTileChoice';
import { getPendingBoardTile, needsBoardTileChoice } from './boardTileEffects';
import { BoardTileRelayPicker } from './BoardTileRelay';
import { getBoardRelaunchRequest, getRelaunchLanding } from './powerUp';
import {
  getMaxHp,
  flipRoll,
  needsMirrorActivation,
  getBaseRoll,
  getBoardAdjustedRoll,
  getBoardTreasureRewards,
  resolveBoardTileChoice,
} from './game';
import { getBoardSquare, getBoardSquareLabel } from './doorBoard';
import {
  BOSS_MAX_HP,
  getCorePhase,
  STANCES,
  chooseStance,
  commitBossBoardRoll,
  commitBossPlayerImpact,
  commitBossPlayerHealing,
  commitBossCounterImpact,
  commitBossCoreHealing,
  lockBossPlayerAttack,
  getBossBoardAdjustedRoll,
  getBossAttack,
  getBossCounterAttack,
  getBossIncomingDamage,
  getBossPlayerHealing,
  getBossRoundHealth,
  getBossIntent,
  nextBossRound,
  resolveBossRound,
  rollBoss,
  rollBossCounter,
} from './boss';
import { playSound } from '../../utils/utils';
import { combatHaptic } from '../../utils/combatHaptics';
import {
  NON_LETHAL_ATTACK_SOUNDS,
  CORE_FIGHT_INTRO_SOUND,
  CORE_DIE_THROW_SOUND,
} from '../../utils/soundEffects';

const PLAYER_DAMAGE_SOUNDS = [
  '/sounds/player-taking-damage.mp3',
  '/sounds/player-taking-damage-2.mp3',
  '/sounds/player-taking-damage-3.mp3',
  '/sounds/player-taking-damage-4.mp3',
];

const EMPTY_BOARD = Object.freeze([]);

export const SparkButton = ({ children, className = '', ...props }) => (
  <button
    className={`coreboundPrimary coreboundSparkButton ${className}`}
    {...props}
  >
    <span className="coreboundButtonSparks" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <i key={i} style={{ '--spark': i }} />
      ))}
    </span>
    {children}
  </button>
);

export { default as Victory } from './Victory';

const formatRoll = formatDieValue;

const CoreDamagePreview = ({ intent }) => <b>{intent.faces.join(' · ')} HP</b>;

const InterferenceNotice = ({ intent }) =>
  intent.interference > 0 ? (
    <div className="coreboundInterferenceNotice" role="status">
      <span aria-hidden="true">≋</span> INTERFERENCE ACTIVE · The Core’s attacks
      are {intent.interference} damage weaker this round.
    </div>
  ) : null;

const getBoardPowerSteps = (game, actor) => {
  const isBoss = actor === 'boss';
  const rawRoll = isBoss ? game.bossRoll : game.roll;
  if (!Number.isInteger(rawRoll)) return [];
  const boardGame = isBoss
    ? {
        ...game,
        doorBoard: game.bossDoorBoard?.map((square) => ({
          ...square,
          coreDamage: true,
        })),
        landingIndex: game.bossLandingIndex,
        chainReactionActivation: null,
        boardTileResolution: null,
      }
    : game;
  const steps = [
    {
      label: 'NUMBER ROLLED',
      detail: 'The value shown when the die settled on the board.',
      value: rawRoll,
    },
  ];
  const initialRoll = isBoss ? rawRoll : getBaseRoll(game);
  if (!isBoss && game.flipped) {
    steps.push({
      label: 'MIRROR CHIP / INITIAL ROLL',
      detail: `${formatRoll(rawRoll)} → ${formatRoll(
        initialRoll
      )} before board or relic effects`,
      value: initialRoll,
    });
  }
  const chainActive = hasChainReaction(boardGame);
  getBoardRollSteps(boardGame, initialRoll).forEach((step, index) => {
    steps.push({
      label: chainActive
        ? `CHAIN REACTION ${index + 1}`
        : `CHAOS BOARD · ${getBoardSquareLabel(step.square)}`,
      detail: `${getBoardSquareLabel(step.square)} · ${formatRoll(
        step.before
      )} → ${formatRoll(step.value)}`,
      value: step.value,
      tileIndex: step.index,
    });
  });
  return steps;
};

const getDamageCalculationRows = (game, actor) => {
  const isBoss = actor === 'boss';
  const naturalRoll = isBoss ? game.bossRoll : getBaseRoll(game);
  const boardRoll = isBoss
    ? getBossBoardAdjustedRoll(game)
    : getBoardAdjustedRoll(game);
  if (!Number.isInteger(naturalRoll) || !Number.isInteger(boardRoll)) return [];

  const attack = isBoss ? null : getBossAttack(game);
  const counter = isBoss ? getBossCounterAttack(game) : null;
  const powerSteps = getBoardPowerSteps(game, actor);
  const rows = powerSteps.map((step) => ({ ...step, dieValue: step.value }));

  if (isBoss) {
    let damageAtStep = boardRoll;
    if (getBossIntent(game).interference > 0) {
      damageAtStep = counter.baseDamage;
      rows.push({
        label: 'INTERFERENCE',
        detail: `Weakens this attack by ${
          getBossIntent(game).interference
        } HP, to a minimum of zero.`,
        value: `${damageAtStep} HP DAMAGE`,
        dieValue: damageAtStep,
      });
    }
    rows.push({
      label: 'DIRECT HP DAMAGE',
      detail: counter.hit
        ? counter.enemyAttackCount === 2
          ? `Trap: two attacks using this face and landing threaten ${counter.baseDamage} HP in total before backlash and defenses.`
          : `The face and landing threaten ${counter.baseDamage} HP before backlash and defenses.`
        : 'Zero damage: the Core misses. No backlash applies.',
      value: counter.hit ? `${counter.baseDamage} HP DAMAGE` : 'MISS · 0 HP',
      dieValue: counter.baseDamage,
    });
    damageAtStep = counter.baseDamage;
    if (counter.stanceBonus) {
      damageAtStep += counter.stanceBonus;
      rows.push({
        label: 'OVERLOAD BACKLASH',
        detail: 'Overload makes a successful counterstrike hurt more.',
        value: `${damageAtStep} DAMAGE`,
        dieValue: damageAtStep,
      });
    }
    if (counter.overclockBonus) {
      damageAtStep += counter.overclockBonus;
      rows.push({
        label: 'OVERCLOCK BACKLASH',
        detail: 'Unsafe power adds incoming damage.',
        value: `${damageAtStep} DAMAGE`,
        dieValue: damageAtStep,
      });
    }
    const coreLanding = getBoardSquare(
      game.bossDoorBoard,
      game.bossLandingIndex
    );
    if (coreLanding?.effect?.type === 'shield') {
      rows.push({
        label: 'CORE SHIELD ARMED',
        detail: `Blocks up to ${coreLanding.effect.amount} damage from your next attack. This round's hit has already landed.`,
        value: `${coreLanding.effect.amount} SHIELD`,
        dieValue: damageAtStep,
      });
    }
    counter.reductions.forEach((reduction) => {
      damageAtStep = Math.max(0, damageAtStep - reduction.amount);
      rows.push({
        label: reduction.id === 'guard' ? 'GUARD' : reduction.id.toUpperCase(),
        detail: reduction.message,
        value: `${damageAtStep} DAMAGE`,
        dieValue: damageAtStep,
      });
    });
    if (counter.decoyIntercepted) {
      rows.push({
        label: 'DECOY BEACON',
        detail: `Your hologram takes the attack instead of you, preventing ${counter.decoyDamage} HP damage. The Core cannot siphon HP from your decoy.`,
        value: `${counter.damage} DAMAGE · DECOY TARGETED`,
        dieValue: counter.damage,
      });
    }
    if (counter.protocolSaved) {
      rows.push({
        label: 'LAST STAND PROTOCOL',
        detail: `Survival roll ${formatRoll(
          game.lastStandProtocol.lastAttempt.finalRoll
        )} beat 75. The lethal hit leaves you at 1 HP. Protocol consumed.`,
        value: `${counter.damage} DAMAGE · SURVIVE AT 1 HP`,
        dieValue: damageAtStep,
      });
    }
    rows.push({
      label: 'FINAL HP LOSS',
      detail: 'The exact HP removed when the counterattack lands.',
      value: `${counter.damage} HP`,
      dieValue: counter.damage,
    });
  } else {
    let runningDamage = attack.baseDamage;
    rows.push({
      label: 'DIRECT DAMAGE BASE',
      detail: attack.frozen
        ? 'Trap freezes the die for this turn. No attack damage.'
        : `${formatRoll(boardRoll)} board power becomes direct damage.`,
      value: `${runningDamage} DAMAGE`,
      dieValue: runningDamage,
    });
    attack.effects
      .filter((effect) => effect.bonus > 0)
      .forEach((effect) => {
        runningDamage += effect.bonus;
        rows.push({
          label: effect.name || effect.id.toUpperCase(),
          relicId: effect.id,
          detail: effect.message,
          value: `${runningDamage} DAMAGE`,
          dieValue: runningDamage,
        });
      });
    if (attack.critical) {
      runningDamage *= 2;
      rows.push({
        label: 'CRITICAL MATRIX',
        relicId: 'criticalMatrix',
        detail: `${attack.damageBeforeCritical} damage doubled.`,
        value: `${runningDamage} DAMAGE`,
        dieValue: runningDamage,
      });
    }
  }

  return rows;
};

const BossBoardRoll = ({
  game,
  actor,
  charge,
  launch,
  reduceMotion,
  onSettled,
  onContinue,
  onFlip,
  onBoardTileChoice,
  onPhaseAnchor,
  onPortalTransit,
  onBoardBump,
  onGhostBump,
  stage,
  onPlayerImpact,
  onStrikeComplete,
  onWebglAvailabilityChange,
  cameraHandoffRef,
}) => {
  const settleTimer = useRef(null);
  const settleNotified = useRef(false);
  const onSettledRef = useRef(onSettled);
  const isBoss = actor === 'boss';
  const calculating = [
    'playerBoard',
    'playerCalculation',
    'bossBoard',
    'bossCalculation',
  ].includes(stage);
  const stageLabels = {
    playerStrike: 'ATTACK IN FLIGHT',
    playerImpact: 'CORE HIT',
    playerOutcome: 'ATTACK RESOLVED',
    bossWarning: 'CORE PREPARING THROW',
    bossCharge: 'COUNTERATTACK CHARGING',
    bossFire: 'COUNTERATTACK INBOUND',
    bossImpact: 'COUNTERATTACK LANDED',
    bossOutcome: 'ROUND RESOLVED',
  };
  const [strikeStartedAt, setStrikeStartedAt] = useState(null);
  useEffect(() => {
    if (stage === 'playerStrike' && strikeStartedAt === null)
      setStrikeStartedAt(Date.now());
  }, [stage, strikeStartedAt]);
  const strikeTiming = getGuardianAttackTiming(
    reduceMotion,
    {
      enemyHpAfter: 1,
      playerHpAfter: 1,
    },
    { immediateReturn: true }
  );
  const ghost = getGhostBumpState(game);
  const intent = getBossIntent(game);
  const landingIndex = isBoss ? game.bossLandingIndex : game.landingIndex;
  const naturalRoll = isBoss ? game.bossRoll : game.roll;
  const attack = isBoss ? null : getBossAttack(game);
  const counter = isBoss ? getBossCounterAttack(game) : null;
  const counterOutcome = getCoreLaserOutcome(
    counter || getBossCounterAttack(game)
  );
  const moment = getBossMoment(
    game,
    stage,
    attack || getBossAttack(game),
    counter || getBossCounterAttack(game)
  );
  const hitSplat =
    !isBoss &&
    (game.bossRoundState?.playerImpactApplied ||
      ['playerImpact', 'playerOutcome'].includes(stage))
      ? {
          hitId: `core-player-${game.bossRound}`,
          damage: attack.damage,
          critical: attack.critical,
          blocked: attack.boardShieldBlocked,
          target: 'Core',
          reduceMotion,
        }
      : isBoss &&
        ['bossImpact', 'bossOutcome'].includes(stage) &&
        (counter.damage > 0 || ['block', 'miss'].includes(counterOutcome))
      ? {
          hitId: `core-counter-${game.bossRound}`,
          damage: counter.damage,
          blocked: counter.reductions.reduce(
            (total, reduction) => total + reduction.amount,
            0
          ),
          target: 'You',
          incoming: true,
          reduceMotion,
        }
      : null;
  const finalAttackValue = isBoss
    ? counter?.damage
    : attack?.damageBeforeShield;
  const relaunchRequest = !isBoss ? getBoardRelaunchRequest(game) : null;
  const [reviewedPowerUp, setReviewedPowerUp] = useState(null);
  const waitingForChainCalculation =
    !!relaunchRequest?.afterChain && reviewedPowerUp !== relaunchRequest.id;
  const activeRelaunch = waitingForChainCalculation ? null : relaunchRequest;
  const settled = Number.isInteger(landingIndex) && !activeRelaunch;
  const board = (isBoss ? game.bossDoorBoard : game.doorBoard) || EMPTY_BOARD;
  const landingSquare = getBoardSquare(board, landingIndex);
  const mirrorActivationPending =
    !isBoss && !relaunchRequest && needsMirrorActivation(game);
  const tileChoicePending =
    !isBoss && !relaunchRequest && needsBoardTileChoice(game);
  const pendingBoardTile = tileChoicePending ? getPendingBoardTile(game) : null;
  const relayChoicePending =
    pendingBoardTile?.type === 'relay' ? pendingBoardTile : null;
  const calculationRows =
    settled && !tileChoicePending && !mirrorActivationPending
      ? getDamageCalculationRows(game, actor)
      : [];
  const calculationRowsSignature = calculationRows
    .map(
      ({ label, tileIndex, dieValue }) =>
        `${label}:${Number.isInteger(tileIndex) ? tileIndex : ''}:${dieValue}`
    )
    .join('|');
  const [visibleCount, setVisibleCount] = useState(0);
  const [isValueFlashing, setIsValueFlashing] = useState(false);
  const playedChainSoundsRef = useRef(new Set());
  const [mirrorChipAnimating, setMirrorChipAnimating] = useState(false);
  const mirrorActivationRef = useRef(null);
  const [treasureRevealStage, setTreasureRevealStage] = useState(0);
  const [treasureRewardIndex, setTreasureRewardIndex] = useState(0);
  const treasureRewards =
    !isBoss && settled ? getBoardTreasureRewards(game) : [];
  const treasureReward = treasureRewards[treasureRewardIndex];
  const chainActive = !isBoss && hasChainReaction(game);
  const triggeredIndices = chainActive
    ? getTriggeredBoardSquares(game).map(({ index }) => index)
    : [];
  const poweringRow = calculationRows[visibleCount];
  const shownRow = calculationRows[Math.max(0, visibleCount - 1)];
  const activeBoardIndex = (isValueFlashing ? poweringRow : shownRow)
    ?.tileIndex;
  const relicCharging = !isBoss && !!poweringRow?.relicId;
  const waitingForTreasure = !!treasureReward && treasureRevealStage < 3;
  const mirrorFromChest =
    mirrorActivationPending &&
    treasureRewards.some(({ id }) => id === 'mirrorChip');
  const calculationsComplete =
    settled &&
    !tileChoicePending &&
    !mirrorActivationPending &&
    !mirrorChipAnimating &&
    !waitingForTreasure &&
    visibleCount >= calculationRows.length;
  const [settledDisplayValue, setSettledDisplayValue] = useState(null);
  const dieCharged =
    calculationsComplete && settledDisplayValue === finalAttackValue;
  const continueRef = useRef(onContinue);
  continueRef.current = onContinue;
  const continuedRef = useRef(false);
  useEffect(() => {
    if (
      !dieCharged ||
      continuedRef.current ||
      ![
        'playerBoard',
        'playerCalculation',
        'bossBoard',
        'bossCalculation',
      ].includes(stage)
    )
      return;
    const timer = setTimeout(
      () => {
        continuedRef.current = true;
        continueRef.current?.();
      },
      reduceMotion ? 0 : 650
    );
    return () => clearTimeout(timer);
  }, [dieCharged, reduceMotion, stage]);
  const currentDieValue = settled
    ? tileChoicePending ||
      mirrorActivationPending ||
      mirrorChipAnimating ||
      waitingForTreasure
      ? naturalRoll
      : calculationsComplete
      ? finalAttackValue
      : calculationRows[Math.max(0, visibleCount - 1)]?.dieValue ?? naturalRoll
    : null;

  useEffect(() => {
    if (!waitingForChainCalculation || !calculationsComplete) return undefined;
    const timer = setTimeout(
      () => setReviewedPowerUp(relaunchRequest.id),
      reduceMotion ? 0 : 350
    );
    return () => clearTimeout(timer);
  }, [
    waitingForChainCalculation,
    calculationsComplete,
    relaunchRequest,
    reduceMotion,
  ]);

  useEffect(() => {
    if (!mirrorActivationPending) mirrorActivationRef.current = null;
  }, [mirrorActivationPending]);

  useEffect(() => {
    if (
      !treasureReward ||
      treasureRevealStage !== 0 ||
      (mirrorActivationPending && !mirrorFromChest)
    )
      return undefined;
    const timer = setTimeout(
      () => setTreasureRevealStage(1),
      TREASURE_TILE_CHARGE_MS
    );
    return () => clearTimeout(timer);
  }, [
    treasureReward,
    treasureRevealStage,
    mirrorActivationPending,
    mirrorFromChest,
  ]);

  useEffect(() => {
    if (
      isBoss ||
      !settled ||
      tileChoicePending ||
      !mirrorActivationPending ||
      (mirrorFromChest && waitingForTreasure)
    )
      return;
    const activationId = `${game.bossRound}:${game.roll}:${game.landingIndex}`;
    if (mirrorActivationRef.current === activationId) return;
    mirrorActivationRef.current = activationId;
    onFlip?.();
    setMirrorChipAnimating(true);
    playSound('/sounds/digital-technology-beep.mp3');
  }, [
    isBoss,
    settled,
    tileChoicePending,
    mirrorActivationPending,
    mirrorFromChest,
    waitingForTreasure,
    onFlip,
    game.bossRound,
    game.roll,
    game.landingIndex,
  ]);

  useEffect(() => {
    if (!mirrorChipAnimating) return undefined;
    const timer = setTimeout(
      () => setMirrorChipAnimating(false),
      reduceMotion ? 450 : 850
    );
    return () => clearTimeout(timer);
  }, [mirrorChipAnimating, reduceMotion]);

  useEffect(() => {
    if (!isBoss) setVisibleCount(0);
  }, [
    game.flipped,
    isBoss,
    game.landingIndex,
    relaunchRequest?.id,
    waitingForChainCalculation,
  ]);

  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  const calculationRowCount = calculationRows.length;
  const nextCalculationTileIndex = calculationRows[visibleCount]?.tileIndex;
  const previousCalculationTileIndex =
    calculationRows[visibleCount - 1]?.tileIndex;
  useEffect(() => {
    if (
      !settled ||
      tileChoicePending ||
      mirrorActivationPending ||
      mirrorChipAnimating ||
      waitingForTreasure ||
      visibleCount >= calculationRowCount
    )
      return undefined;
    const nextTileIndex = nextCalculationTileIndex;
    const cueKey = `${game.bossRound}:${game.roll}:${landingIndex}:${nextTileIndex}`;
    if (
      chainActive &&
      Number.isInteger(nextTileIndex) &&
      nextTileIndex !== previousCalculationTileIndex &&
      !playedChainSoundsRef.current.has(cueKey)
    ) {
      playedChainSoundsRef.current.add(cueKey);
      playSound('/sounds/spell-casting.mp3', {
        preventOverlap: true,
      });
    }
    let revealTimer;
    const timer = setTimeout(
      () => {
        const advanceCalculation = () => {
          setVisibleCount((count) => Math.min(calculationRowCount, count + 1));
          setIsValueFlashing(false);
        };
        if (visibleCount === 0 || reduceMotion) {
          advanceCalculation();
          return;
        }
        setIsValueFlashing(true);
        revealTimer = setTimeout(advanceCalculation, chainActive ? 80 : 220);
      },
      reduceMotion ? 0 : chainActive ? 180 : 360
    );
    return () => {
      clearTimeout(timer);
      if (revealTimer) clearTimeout(revealTimer);
    };
  }, [
    settled,
    tileChoicePending,
    mirrorActivationPending,
    mirrorChipAnimating,
    waitingForTreasure,
    visibleCount,
    calculationRowCount,
    nextCalculationTileIndex,
    previousCalculationTileIndex,
    calculationRowsSignature,
    reduceMotion,
    chainActive,
    game.bossRound,
    game.roll,
    landingIndex,
  ]);

  const settleOnce = (
    nextLandingIndex,
    settledRoll = naturalRoll,
    relaunchId = null,
    faceValues = null
  ) => {
    if (settleNotified.current || !Number.isInteger(nextLandingIndex)) return;
    settleNotified.current = true;
    onSettledRef.current(nextLandingIndex, settledRoll, relaunchId, faceValues);
  };

  useEffect(() => {
    settleNotified.current = settled;
    const failsafeTimer = setTimeout(
      () => {
        if (settleNotified.current || !board.length) return;
        const fallbackIndex = relaunchRequest
          ? getRelaunchLanding(board, relaunchRequest.index)
          : Number.isInteger(landingIndex)
          ? landingIndex
          : launch && !isBoss
          ? getSlingshotFallbackTile(launch, null)
          : Math.floor(Math.random() * board.length);
        settleNotified.current = true;
        onSettledRef.current(
          fallbackIndex,
          relaunchRequest?.after ?? naturalRoll,
          relaunchRequest?.id
        );
      },
      reduceMotion && isBoss && !ghost.owned
        ? 1000
        : 10000 +
            (isBoss
              ? ghost.owned
                ? BOARD_BUMP_SETTLE_TIME * 1000
                : 0
              : MAX_BOARD_BUMPS * BOARD_BUMP_SETTLE_TIME * 1000 +
                PHASE_ANCHOR_DURATION_MS)
    );
    return () => {
      clearTimeout(failsafeTimer);
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [
    actor,
    game.bossRound,
    settled,
    landingIndex,
    board,
    naturalRoll,
    reduceMotion,
    isBoss,
    launch,
    ghost.owned,
    relaunchRequest,
  ]);

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    },
    []
  );

  return (
    <div
      className={`coreboundBossBoardArena coreboundBossBoardCinematic coreboundBossBoardArena-${actor} ${
        settled ? 'is-settled' : ''
      }`}
    >
      <GlitchPrismScene
        boardStatus={
          <div className="coreboundBossBoardHeader">
            <strong>
              {stageLabels[stage] ||
                (settled
                  ? dieCharged
                    ? 'FULLY CHARGED'
                    : 'CHARGING THE DIE'
                  : isBoss
                  ? 'COUNTER-DIE IN FLIGHT'
                  : 'DIE IN FLIGHT')}
            </strong>
            {isBoss && (
              <small>
                CORE DIE · {getCoreDieAppearance(getCorePhase(game)).name}
              </small>
            )}
            <InterferenceNotice intent={intent} />
          </div>
        }
        sector={game.floor}
        key={`${actor}-${game.bossRound}`}
        dieType={isBoss ? undefined : game.selectedDie}
        corePhase={getCorePhase(game)}
        playerHealth={{ hp: game.hp, maxHp: getMaxHp(game) }}
        coreBoss={{
          cameraHandoffRef,
          hp: game.bossHp,
          shield: game.coreShield,
          phase: getCorePhase(game),
          stage,
          hitSplat,
          damageFaces: intent.faces,
          outcome: counterOutcome,
          attackType: counter?.attackType,
          playerDie: isBoss
            ? {
                dieId: game.selectedDie,
                faces: getCoreDieFaceValues(game),
                style: game.stance,
              }
            : null,
          onPlayerImpact,
          onStrikeComplete,
          combat:
            !isBoss && strikeStartedAt !== null
              ? {
                  ...strikeTiming,
                  combatId: `core-${game.bossRound}`,
                  startTime: strikeStartedAt,
                  effectiveRoll: attack.damageBeforeShield,
                  frozen: attack.frozen,
                  enemyHpAfter: game.bossHp,
                  retaliation: false,
                  relicsBefore: game.relics,
                }
              : null,
        }}
        onWebglAvailabilityChange={onWebglAvailabilityChange}
        phase={settled ? 'revealed' : 'rolling'}
        charge={charge}
        attackStyle={isBoss || game.coreStyleVersion !== 1 ? null : game.stance}
        dieFaceValues={isBoss ? null : getCoreDieFaceValues(game)}
        relaunch={activeRelaunch}
        trapEffect={
          !isBoss && settled && !tileChoicePending
            ? getTriggeredBoardSquares(game).find(
                ({ square }) => square.effect?.type === 'trap'
              )?.square.effect
            : null
        }
        shieldAmount={
          !settled || tileChoicePending
            ? 0
            : isBoss
            ? landingSquare?.effect?.type === 'shield'
              ? landingSquare.effect.amount
              : 0
            : getBoardEffectTotal(game, 'shield')
        }
        launch={isBoss ? null : launch}
        rollLabel={settled ? formatRoll(currentDieValue) : '??'}
        onValueSettled={setSettledDisplayValue}
        rollValue={naturalRoll}
        powerUp={isBoss ? null : game.powerUp}
        rollReady
        doorBoard={board}
        boardCondition={null}
        allowBumps={!isBoss}
        ghostBumpArmed={isBoss && ghost.canUse}
        onGhostBump={onGhostBump}
        phaseAnchorArmed={!isBoss && getPhaseAnchorState(game).available}
        onPhaseAnchor={onPhaseAnchor}
        portalPair={!isBoss ? getPortalPairState(game) : null}
        onPortalTransit={onPortalTransit}
        onBoardBump={onBoardBump}
        concealFacesUntilSettled={isBoss}
        boardPowering={isValueFlashing && Number.isInteger(activeBoardIndex)}
        relicPowering={isValueFlashing && !!relicCharging}
        relicShine={calculationsComplete && !isBoss}
        boardShine={calculationsComplete && isBoss}
        boardActiveIndex={activeBoardIndex}
        chainReactionTiles={chainActive ? triggeredIndices : null}
        focusSettledDie={settled}
        settledLandingIndex={landingIndex}
        treasureChargeIndex={
          treasureReward &&
          treasureRevealStage === 0 &&
          (!mirrorActivationPending || mirrorFromChest)
            ? treasureReward.index ?? landingIndex
            : null
        }
        onImpact={() => {
          playSound('/sounds/low-frequency-dice-impact.mp3');
          if (isBoss) playSound('/sounds/glitch_core_die_settled.mp3');
        }}
        onAnimationComplete={(
          finalRoll,
          nextLandingIndex,
          relaunchId,
          faceValues
        ) => {
          if (
            !settled &&
            Number.isInteger(nextLandingIndex) &&
            !settleNotified.current
          ) {
            settleTimer.current = setTimeout(
              () =>
                settleOnce(nextLandingIndex, finalRoll, relaunchId, faceValues),
              reduceMotion ? 0 : 450
            );
          }
        }}
      />
      {relayChoicePending && (
        <BoardTileRelayPicker
          board={board}
          relay={relayChoicePending}
          onChoose={onBoardTileChoice}
        />
      )}
      {calculating &&
        settled &&
        !tileChoicePending &&
        !mirrorChipAnimating &&
        !waitingForTreasure && (
          <div className="coreboundCoreBoardCharge" role="status">
            <small>
              {isBoss
                ? calculationsComplete
                  ? 'FINAL HP LOSS'
                  : shownRow?.label || 'DAMAGE DIE'
                : dieCharged
                ? 'ATTACK READY'
                : relicCharging
                ? 'RELIC SURGE'
                : 'LANDING TILE'}
            </small>
            <strong>
              {isBoss
                ? `${
                    calculationsComplete
                      ? counter.damage
                      : currentDieValue ?? naturalRoll
                  } HP`
                : dieCharged
                ? 'Signal locked. Unleashing…'
                : calculationsComplete
                ? 'Charging to full power…'
                : relicCharging
                ? poweringRow.label
                : getBoardSquareLabel(landingSquare)}
            </strong>
            {isBoss && shownRow && <small>{shownRow.detail}</small>}
          </div>
        )}
      {settled && !isBoss && tileChoicePending && !relayChoicePending && (
        <BoardTileChoice
          game={game}
          onChoose={onBoardTileChoice}
          reduceMotion={reduceMotion}
        />
      )}
      {moment && (
        <div
          className={`coreboundBattleMoment ${
            actor === 'boss' ? 'is-defense' : ''
          }`}
          role="status"
          key={`${game.bossRound}-${actor}`}
          style={reduceMotion ? { animation: 'none' } : undefined}
        >
          {moment}
        </div>
      )}
      {treasureReward && treasureRevealStage === 1 && (
        <BoardTreasureReveal
          key={`${treasureRewardIndex}-${treasureReward.id}`}
          reward={treasureReward}
          stage={1}
          reduceMotion={reduceMotion}
          onComplete={() => {
            if (treasureRewardIndex + 1 < treasureRewards.length) {
              setTreasureRewardIndex((index) => index + 1);
              setTreasureRevealStage(0);
            } else setTreasureRevealStage(3);
          }}
        />
      )}
      {mirrorChipAnimating && (
        <MirrorChipActivation
          before={naturalRoll}
          after={getBaseRoll(game)}
          reduceMotion={reduceMotion}
        />
      )}
      {calculating &&
        settled &&
        !isBoss &&
        (relicCharging || calculationsComplete) &&
        !tileChoicePending &&
        attack.effects.length > 0 && (
          <div
            className="coreboundRelicChargeFeed"
            role="status"
            aria-label="Relics charging the die"
          >
            <small>RELIC SURGE</small>
            <div>
              {attack.effects.map((effect) => (
                <span key={effect.id}>
                  <RelicIcon
                    relicId={effect.id}
                    icon={effect.icon || RELICS[effect.id]?.icon}
                  />
                  <span>{effect.name || RELICS[effect.id]?.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

const resolveBossRoundWithBreakdown = (game) => {
  if (!game.bossRoundState?.damageCalculation) return resolveBossRound(game);
  return resolveBossRound({
    ...game,
    bossRoundState: {
      ...game.bossRoundState,
      counterCalculationRows: getDamageCalculationRows(game, 'boss'),
    },
  });
};

const getBossResumeStage = (game) => {
  const round =
    game.bossRoundState?.round === game.bossRound ? game.bossRoundState : null;
  if (game.bossHp === 0) return 'coreCollapse';
  if (round?.counterImpactApplied) return 'bossOutcome';
  if (round?.playerImpactApplied)
    return game.phase === 'bossRolled'
      ? Number.isInteger(game.bossLandingIndex)
        ? 'bossCalculation'
        : 'bossBoard'
      : 'playerOutcome';
  if (round?.attack) return 'playerStrike';
  return Number.isInteger(game.landingIndex)
    ? 'playerCalculation'
    : 'playerBoard';
};

const BossBattle = ({ game, setGame, reduceMotion }) => {
  const [stage, setStage] = useState('waiting');
  // Only a saved in-progress round needs the explicit recovery action. A live
  // presentation reset must continue from committed impacts automatically.
  const [recoveryPending, setRecoveryPending] = useState(() =>
    ['bossPlayerRolled', 'bossRolled'].includes(game.phase)
  );
  const [playerImpactComplete, setPlayerImpactComplete] = useState(false);
  const [finaleHandoffComplete, setFinaleHandoffComplete] = useState(false);
  const [coreWebglAvailable, setCoreWebglAvailable] = useState(true);
  const [launchCharge, setLaunchCharge] = useState(() =>
    game.slingshotLaunch
      ? normalizeSlingshotLaunch(game.slingshotLaunch).power
      : 0
  );
  const [rollLaunch, setRollLaunch] = useState(() =>
    game.slingshotLaunch ? normalizeSlingshotLaunch(game.slingshotLaunch) : null
  );
  const boardTransitionTimer = useRef(null);
  const cameraHandoffRef = useRef(null);
  const damageSoundRoundRef = useRef(null);
  const coreThrowSoundRoundRef = useRef(null);
  const playerImpactSoundRoundRef = useRef(null);
  const coreGreetingPlayedRef = useRef(false);
  const interferenceRef = useRef(game.boardTileResolution?.interference || 0);
  const corePhase = getCorePhase(game);
  const announcedCorePhaseRef = useRef(corePhase);
  const intent = getBossIntent(game);
  const interference = game.boardTileResolution?.interference || 0;
  const nextIntent = getBossIntent({ ...game, bossRound: game.bossRound + 1 });
  const threat = useMemo(
    () => (stage === 'bossWarning' ? getCoreThreat(game) : null),
    [game, stage]
  );
  const attack = getBossAttack(game);
  const combatSummary = (stance) =>
    ({
      strike: 'Normal power. Normal damage taken.',
      guard: 'Power −25%. Block 1 damage.',
      overload: 'Power +50%. Take 3 extra damage when hit.',
    }[stance]);
  const ready = game.phase === 'bossReady';
  const result = game.phase === 'bossResult';
  const phaseSpellPending = !!game.corePhaseSpell;
  const showPhaseSpell =
    phaseSpellPending &&
    (ready || ['playerOutcome', 'bossWarning'].includes(stage));
  const protocolPending = !!game.lastStandProtocol?.pending;
  const counterAttack = getBossCounterAttack(game);
  const massiveCoreAttack =
    counterAttack.hit && counterAttack.attackType === 'massiveAttack';
  const incomingDamage = getBossIncomingDamage(game);
  const attackBlocked =
    counterAttack.reductions.some(({ amount }) => amount > 0) ||
    (counterAttack.hit &&
      incomingDamage === 0 &&
      !counterAttack.decoyIntercepted);
  const laserOutcome = getCoreLaserOutcome(counterAttack);
  const laserActive =
    !ready &&
    !result &&
    !protocolPending &&
    ['bossCharge', 'bossFire', 'bossImpact', 'bossOutcome'].includes(stage);
  const { healed } = getBossPlayerHealing(game);
  const roundHealth = getBossRoundHealth(game);
  const hpBeforeImpact = roundHealth.hpAfterHealing;
  const hpAfterImpact = roundHealth.hpAfterImpact;
  const shownMaxHp = getMaxHp(game);

  useEffect(() => {
    if (interference > interferenceRef.current)
      playSound('/sounds/interference.mp3');
    interferenceRef.current = interference;
  }, [interference]);
  const shownPlayerHp = game.hp;
  const shownBossHp = game.bossHp;
  const lethal = roundHealth.bossHpAfterPlayerImpact === 0;
  const playPlayerImpactSound = useCallback(() => {
    if (playerImpactSoundRoundRef.current === game.bossRound) return;
    playerImpactSoundRoundRef.current = game.bossRound;
    if (attack.damage > 0) combatHaptic('dealt');
    playSound(
      lethal
        ? '/sounds/low-frequency-dice-impact.mp3'
        : NON_LETHAL_ATTACK_SOUNDS[
            Math.floor(Math.random() * NON_LETHAL_ATTACK_SOUNDS.length)
          ],
      { preventOverlap: true }
    );
  }, [lethal, game.bossRound, attack.damage]);
  const coreFinaleActive = !protocolPending && stage === 'coreCollapse';
  const finaleFaces = useMemo(
    () => game.powerUp?.faceValues || getCoreDieFaceValues(game),
    [game]
  );
  const finishFinaleHandoff = useCallback(
    () => setFinaleHandoffComplete(true),
    []
  );
  const boardStage = coreFinaleActive
    ? lethal
      ? 'playerImpact'
      : 'bossCalculation'
    : stage;
  const playerBoard = [
    'playerBoard',
    'playerCalculation',
    'playerStrike',
    'playerImpact',
    'playerOutcome',
    'bossWarning',
  ].includes(boardStage);
  useEffect(() => {
    if (!ready || game.bossRound !== 1 || coreGreetingPlayedRef.current) return;
    coreGreetingPlayedRef.current = true;
    playSound(CORE_FIGHT_INTRO_SOUND, {
      preventOverlap: true,
    });
  }, [ready, game.bossRound]);

  useEffect(() => {
    if (coreFinaleActive) return;
    if (announcedCorePhaseRef.current === corePhase) return;
    announcedCorePhaseRef.current = corePhase;

    playSound(
      corePhase === 'meltdown'
        ? '/sounds/boss_enraged.mp3'
        : '/sounds/boss_enraged_levels_1-3.mp3',
      { preventOverlap: true }
    );
  }, [corePhase, coreFinaleActive]);

  useEffect(() => {
    const damage = stage === 'bossImpact' && !ready ? incomingDamage : 0;
    if (!damage) return;

    const round = game.bossRound;
    if (damageSoundRoundRef.current === round) return;
    damageSoundRoundRef.current = round;

    const sound =
      PLAYER_DAMAGE_SOUNDS[
        Math.floor(Math.random() * PLAYER_DAMAGE_SOUNDS.length)
      ];
    playSound(sound, { preventOverlap: true });
    combatHaptic('received');
  }, [stage, incomingDamage, ready, game.bossRound]);
  const cinematicBattle =
    !ready &&
    !result &&
    [
      'playerBoard',
      'playerCalculation',
      'playerStrike',
      'playerImpact',
      'playerOutcome',
      'bossWarning',
      'bossSpit',
      'bossBoard',
      'bossCalculation',
      'bossCharge',
      'bossFire',
      'bossImpact',
      'bossOutcome',
      'coreCollapse',
    ].includes(stage);
  const finishBossWarning = useCallback(() => {
    setGame((current) =>
      current.phase === 'bossPlayerRolled'
        ? rollBossCounter(current, rollCoreDamage(getBossIntent(current).faces))
        : current
    );
    setStage((current) => (current === 'bossWarning' ? 'bossBoard' : current));
  }, [setGame]);

  useEffect(() => {
    if (game.phase !== 'bossRolled') return;
    if (stage === 'bossCharge') {
      setGame(commitBossPlayerHealing);
      return;
    }
    const commit = {
      bossImpact: (current) => {
        const next = commitBossCounterImpact(current);
        if (
          !next.lastStandProtocol?.pending ||
          current.lastStandProtocol?.pending
        )
          return next;
        return {
          ...next,
          lastStandProtocol: {
            ...next.lastStandProtocol,
            pending: { ...next.lastStandProtocol.pending, impactSeen: true },
          },
        };
      },
      bossOutcome: commitBossCoreHealing,
    }[stage];
    if (commit) setGame(commit);
  }, [stage, game.phase, setGame, protocolPending, counterAttack.attackType]);

  useEffect(() => {
    if (protocolPending && game.phase === 'bossRolled' && stage === 'waiting')
      setStage('bossImpact');
  }, [protocolPending, game.phase, stage]);

  useEffect(() => {
    // The shared board owns impact timing. Finish even if WebGL is unavailable
    // or a suspended renderer misses its contact callback.
    if (stage !== 'playerStrike') return undefined;
    playSound('/sounds/attack.mp3');
    const timer = setTimeout(
      () => {
        playPlayerImpactSound();
        setGame(commitBossPlayerImpact);
        setPlayerImpactComplete(true);
        setStage('playerImpact');
      },
      reduceMotion ? 0 : process.env.NODE_ENV === 'test' ? 450 : 4500
    );
    return () => clearTimeout(timer);
  }, [stage, reduceMotion, lethal, setGame, playPlayerImpactSound]);

  useEffect(() => {
    if (stage !== 'playerImpact' || !playerImpactComplete) return undefined;
    const timer = setTimeout(() => {
      setStage(lethal ? 'coreCollapse' : 'playerOutcome');
    }, 0);
    return () => clearTimeout(timer);
  }, [stage, playerImpactComplete, reduceMotion, lethal]);

  useEffect(() => {
    if (stage !== 'playerOutcome' || phaseSpellPending) return undefined;
    setGame(commitBossPlayerHealing);
    const timer = setTimeout(
      () => {
        playSound('/sounds/epic-get-ready.mp3');
        setStage('bossWarning');
      },
      reduceMotion && !healed ? 0 : 650
    );
    return () => clearTimeout(timer);
  }, [stage, reduceMotion, setGame, phaseSpellPending, healed]);

  useEffect(() => {
    if (
      ['bossSpit', 'bossBoard'].includes(stage) &&
      game.phase === 'bossRolled'
    ) {
      setGame(startDecoyBeaconThrow);
    }
  }, [stage, game.phase, setGame]);

  useEffect(() => {
    if (stage === 'bossBoard' && game.phase === 'bossRolled') {
      setGame(startGhostBumpThrow);
    }
  }, [stage, game.phase, setGame]);

  useEffect(() => {
    if (
      stage !== 'bossBoard' ||
      game.phase !== 'bossRolled' ||
      Number.isInteger(game.bossLandingIndex) ||
      coreThrowSoundRoundRef.current === game.bossRound
    )
      return;
    coreThrowSoundRoundRef.current = game.bossRound;
    playSound(CORE_DIE_THROW_SOUND, { preventOverlap: true });
  }, [stage, game.phase, game.bossRound, game.bossLandingIndex]);

  useEffect(() => {
    const nextStage = {
      bossCharge: 'bossFire',
      bossFire: 'bossImpact',
      bossImpact: 'bossOutcome',
    }[stage];
    if (!nextStage || game.phase !== 'bossRolled' || protocolPending)
      return undefined;
    const timer = setTimeout(
      () => setStage((current) => (current === stage ? nextStage : current)),
      getCoreLaserDuration(
        stage,
        reduceMotion,
        counterAttack.hit ? counterAttack.attackType : null
      )
    );
    return () => clearTimeout(timer);
  }, [
    stage,
    game.phase,
    reduceMotion,
    protocolPending,
    counterAttack.hit,
    counterAttack.attackType,
  ]);

  useEffect(() => {
    if (stage === 'bossFire') playSound('/sounds/instant-explosion-attack.mp3');
    if (stage !== 'bossImpact') return;
    if (attackBlocked)
      playSound('/sounds/shield-used.mp3', { preventOverlap: true });
    if (laserOutcome === 'decoy') playSound('/sounds/decoy-intercept.mp3');
    else if (laserOutcome === 'protocol')
      playSound('/sounds/digital-technology-beep.mp3');
    else if (laserOutcome === 'miss') playSound('/sounds/futuristic-zoom.mp3');
  }, [stage, laserOutcome, attackBlocked]);

  const start = (launch) => {
    setRecoveryPending(false);
    setFinaleHandoffComplete(false);
    playSound('/sounds/dice-roll-throw.mp3');
    setLaunchCharge(launch.power);
    setRollLaunch(launch);
    setPlayerImpactComplete(false);
    setStage('playerBoard');
    setGame((current) => ({
      ...rollBoss(current, rollDie(current.selectedDie)),
      slingshotLaunch: launch,
    }));
  };

  useEffect(
    () => () => {
      if (boardTransitionTimer.current)
        clearTimeout(boardTransitionTimer.current);
    },
    []
  );

  useLayoutEffect(() => {
    if (!ready) return;
    setStage('waiting');
    setLaunchCharge(0);
    setRollLaunch(null);
  }, [ready]);

  useEffect(() => {
    if (
      stage === 'waiting' &&
      !recoveryPending &&
      !protocolPending &&
      ['bossPlayerRolled', 'bossRolled'].includes(game.phase)
    )
      setStage(getBossResumeStage(game));
  }, [stage, recoveryPending, protocolPending, game]);

  useEffect(() => {
    if (game.phase === 'bossResult') setGame(nextBossRound);
  }, [game.phase, setGame]);

  const finishCoreFinale = useCallback(() => {
    setGame((current) => {
      if (current.phase === 'won') return current;
      const resolved = resolveBossRoundWithBreakdown(current);
      if (resolved.phase !== 'won') return resolved;
      return {
        ...resolved,
        lastResult: { ...resolved.lastResult, liberationSeen: true },
      };
    });
  }, [setGame]);

  useEffect(() => {
    if (
      stage !== 'bossOutcome' ||
      game.phase !== 'bossRolled' ||
      protocolPending
    )
      return undefined;
    const timer = setTimeout(
      () =>
        setGame((current) =>
          nextBossRound(resolveBossRoundWithBreakdown(current))
        ),
      getCoreLaserDuration('bossOutcome', reduceMotion)
    );
    return () => clearTimeout(timer);
  }, [stage, game.phase, reduceMotion, setGame, protocolPending]);

  const settleBoard = (
    actor,
    landingIndex,
    settledRoll,
    relaunchId,
    faceValues
  ) => {
    const nextStage =
      actor === 'boss' ? 'bossCalculation' : 'playerCalculation';
    setGame((current) =>
      commitBossBoardRoll(
        current,
        landingIndex,
        actor,
        settledRoll,
        Math.random,
        relaunchId,
        faceValues
      )
    );
    if (actor === 'player') setPlayerImpactComplete(false);
    if (boardTransitionTimer.current)
      clearTimeout(boardTransitionTimer.current);
    boardTransitionTimer.current = setTimeout(
      () =>
        setStage((current) =>
          current === (actor === 'boss' ? 'bossBoard' : 'playerBoard')
            ? nextStage
            : current
        ),
      reduceMotion ? 0 : process.env.NODE_ENV === 'test' ? 250 : 900
    );
  };

  return (
    <section
      className={`coreboundPanel coreboundBattle ${
        corePhase === 'meltdown' ? 'coreboundBattleEnraged' : ''
      } coreboundBattlePhase-${corePhase} ${
        cinematicBattle ? 'coreboundBattleCinematic' : ''
      } ${ready ? 'coreboundBattleReady' : ''}`}
      aria-label="Glitch Core battle"
    >
      <div className="coreboundBattleHeader">
        {ready ? (
          <div className="coreboundPanelHeadingRow coreboundBattleSetupHeading">
            <div className="coreboundBattleSetupKicker">
              <span className="coreboundPanelKicker">FINAL ENCOUNTER</span>
              <span>ROUND {game.bossRound}</span>
            </div>
          </div>
        ) : (
          <>
            <span className="coreboundPanelKicker">FINAL ENCOUNTER</span>
            <span>ROUND {game.bossRound}</span>
          </>
        )}
      </div>
      <h2>The Glitch Core</h2>
      <div className="coreboundCombatants">
        <div>
          <span>YOU</span>
          <strong>
            {shownPlayerHp} / {shownMaxHp} HP
          </strong>
          <div
            className="coreboundHealth"
            role="progressbar"
            aria-label="Your combat HP"
            aria-valuemin={0}
            aria-valuemax={shownMaxHp}
            aria-valuenow={shownPlayerHp}
          >
            <span style={{ width: `${(shownPlayerHp / shownMaxHp) * 100}%` }} />
          </div>
        </div>
        <span className="coreboundVersus">VS</span>
        <div>
          <span>CORE · {getCoreAppearance(corePhase).name}</span>
          <strong>
            {shownBossHp} / {BOSS_MAX_HP} HP
            <CoreShieldValue shield={game.coreShield} />
          </strong>
          <div
            className="coreboundHealth coreboundBossHealth"
            role="progressbar"
            aria-label="Glitch Core HP"
            aria-valuemin={0}
            aria-valuemax={BOSS_MAX_HP}
            aria-valuenow={shownBossHp}
          >
            <CoreHealthFill
              hp={shownBossHp}
              shield={game.coreShield}
              reduceMotion={reduceMotion}
            />
          </div>
        </div>
      </div>
      {coreFinaleActive && (
        <CoreFinalBlow
          dieId={game.selectedDie}
          critical={attack.critical}
          damage={attack.damage}
          faceValues={finaleFaces}
          impactComplete={!!game.bossRoundState?.playerImpactApplied && lethal}
          onHandoffComplete={finishFinaleHandoff}
          reduceMotion={reduceMotion}
          onImpact={undefined}
          onComplete={finishCoreFinale}
        />
      )}
      {showPhaseSpell && (
        <CorePhaseSpell
          key={game.corePhaseSpell.id}
          game={game}
          spell={game.corePhaseSpell}
          reduceMotion={reduceMotion}
          onComplete={(id) =>
            setGame((current) => completeCorePhaseSpell(current, id))
          }
        />
      )}
      {ready && !phaseSpellPending && (
        <SlingshotLauncher
          sector={game.floor}
          relics={game.relics}
          attackRecap={
            game.lastCoreAttack && (
              <GuardianDamageCalculation
                key={game.lastCoreAttack.round}
                result={game.lastCoreAttack}
              />
            )
          }
          playerHealth={{ hp: shownPlayerHp, maxHp: shownMaxHp }}
          coreBoss={{
            hp: shownBossHp,
            shield: game.coreShield,
            phase: corePhase,
            cameraHandoffRef,
          }}
          arrival={ready}
          aimTitle={`Your attack · Round ${game.bossRound}`}
          attackStyle={game.stance}
          dieFaceValues={getCoreDieFaceValues(game)}
          combatSummary={combatSummary(game.stance)}
          combatSummaryOptions={Object.keys(STANCES).map(combatSummary)}
          combatControls={
            <>
              <div className="coreboundSlingshotStyleChoices">
                <span className="coreboundSlingshotCombatLabel">
                  Attack style
                </span>
                <div
                  className="coreboundStances"
                  role="group"
                  aria-label="Combat stance"
                >
                  {Object.entries(STANCES).map(([id, stance]) => (
                    <button
                      key={id}
                      type="button"
                      className={`coreboundChoice coreboundStance-${id}`}
                      aria-label={`${stance.name}. ${stance.description}`}
                      title={stance.description}
                      aria-pressed={game.stance === id}
                      onClick={() => {
                        playSound(
                          id === 'overload'
                            ? '/sounds/overload-on.mp3'
                            : id === 'guard'
                            ? '/sounds/shield-charge-up.mp3'
                            : game.stance === 'overload'
                            ? '/sounds/overload-off.mp3'
                            : '/sounds/menu-select.mp3'
                        );
                        setGame((current) => chooseStance(current, id));
                      }}
                    >
                      <strong>
                        <span
                          className="coreboundStanceSymbol"
                          aria-hidden="true"
                        >
                          {stance.symbol}
                        </span>
                        <span>{stance.name}</span>
                      </strong>
                      <span>{stance.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          }
          selectedDie={game.selectedDie}
          onSelectDie={(id) => setGame((current) => selectDie(current, id))}
          doorBoard={game.doorBoard}
          tileInfoContext={{
            core: true,
            shards: game.shards,
          }}
          boardCondition={null}
          portalPair={getPortalPairState(game)}
          chainReactionReady={getChainReactionState(game).armed}
          relicControls={
            <RelicAbilityBar
              game={game}
              onToggleChainReaction={() => setGame(toggleChainReaction)}
              onToggleDecoyBeacon={() => {
                if (!getDecoyBeaconState(game).canDeploy) return;
                playSound('/sounds/deploy-beacon.mp3');
                setGame(toggleDecoyBeacon);
              }}
            />
          }
          onLaunch={start}
          label="ATTACK"
        />
      )}
      {!ready &&
        !result &&
        !showPhaseSpell &&
        (!coreFinaleActive || !finaleHandoffComplete) &&
        stage !== 'waiting' &&
        !protocolPending && (
          <BossBoardRoll
            key={`${game.bossRound}-${playerBoard ? 'player' : 'boss'}`}
            game={game}
            stage={boardStage}
            cameraHandoffRef={cameraHandoffRef}
            onWebglAvailabilityChange={setCoreWebglAvailable}
            onPlayerImpact={() => {
              playPlayerImpactSound();
              setGame(commitBossPlayerImpact);
              if (lethal) setStage('coreCollapse');
            }}
            onStrikeComplete={() => {
              playPlayerImpactSound();
              setGame(commitBossPlayerImpact);
              setPlayerImpactComplete(true);
              setStage((current) =>
                current === 'playerStrike'
                  ? lethal
                    ? 'coreCollapse'
                    : 'playerImpact'
                  : current
              );
            }}
            actor={playerBoard ? 'player' : 'boss'}
            charge={stage === 'playerBoard' ? launchCharge : 0.9}
            launch={rollLaunch}
            reduceMotion={reduceMotion}
            onFlip={() => setGame(flipRoll)}
            onBoardTileChoice={(choice) =>
              setGame((current) => resolveBoardTileChoice(current, choice))
            }
            onPhaseAnchor={() => setGame(activatePhaseAnchor)}
            onPortalTransit={() => setGame(activatePortalPair)}
            onBoardBump={() =>
              setGame((current) => recordThrowEvent(current, 'bump'))
            }
            onGhostBump={() => setGame(activateGhostBump)}
            onContinue={() => {
              if (
                ['playerBoard', 'playerCalculation'].includes(stage) &&
                !needsBoardTileChoice(game) &&
                !needsMirrorActivation(game)
              ) {
                setGame((current) => {
                  const locked = lockBossPlayerAttack(current);
                  if (!locked.bossRoundState) return locked;
                  return {
                    ...locked,
                    bossRoundState: {
                      ...locked.bossRoundState,
                      damageCalculation: {
                        initialRoll: getBaseRoll(current),
                        boardRoll: getBoardAdjustedRoll(current),
                        finalRoll: getBossAttack(current).damageBeforeShield,
                        boardSteps: [],
                        relicEffects: [],
                        rows: getDamageCalculationRows(current, 'player'),
                      },
                    },
                  };
                });
                setStage('playerStrike');
              } else if (['bossBoard', 'bossCalculation'].includes(stage)) {
                setGame((current) => ({
                  ...current,
                  bossRoundState: {
                    ...current.bossRoundState,
                    counterCalculationRows: getDamageCalculationRows(
                      current,
                      'boss'
                    ),
                  },
                }));
                const counter = getBossCounterAttack(game);
                setStage(counter.won ? 'coreCollapse' : 'bossCharge');
              }
            }}
            onSettled={(landingIndex, settledRoll, relaunchId, faceValues) =>
              settleBoard(
                stage === 'playerBoard' ||
                  stage === 'playerCalculation' ||
                  stage === 'playerOutcome'
                  ? 'player'
                  : 'boss',
                landingIndex,
                settledRoll,
                relaunchId,
                faceValues
              )
            }
          />
        )}
      {stage === 'bossWarning' && !phaseSpellPending && (
        <div
          className="coreboundBossWarning"
          role="region"
          aria-label="Prepare for the Core's turn"
        >
          <span className="coreboundPanelKicker">BRACE YOURSELF</span>
          <strong>ATTACK INCOMING</strong>
          <CoreAttackDiePreview intent={intent} reduceMotion={reduceMotion} />
          <p>
            <strong>{intent.name}</strong>:{' '}
            <CoreDamagePreview intent={intent} />.
          </p>

          <InterferenceNotice intent={intent} />
          {game.coreBoardPhase && game.coreBoardPhase !== 'containment' && (
            <p className="coreboundCorePhaseNotice">
              Core power tiles: +
              {
                { fracture: 20, emergence: 40, evolution: 60, meltdown: 80 }[
                  game.coreBoardPhase
                ]
              }
              % damage.{' '}
              {
                game.bossDoorBoard.filter(
                  (square) => square.effect?.type === 'massiveAttack'
                ).length
              }{' '}
              MASSIVE ATTACK tiles on this board.
            </p>
          )}
          <p className="coreboundCounterplay">
            {getCoreCounterplay(intent.name)}
          </p>
          {threat && (
            <div
              className={`coreboundThreatReadout ${
                threat.lethal ? 'is-danger' : ''
              }`}
              role="status"
            >
              <strong>
                {threat.lethal ? 'POTENTIALLY LETHAL' : 'DAMAGE FORECAST'}
              </strong>
              <span>
                Possible damage: {threat.min}–{threat.max} HP, including board
                effects and current defenses.
              </span>
              {threat.lethal && getDecoyBeaconState(game).canDeploy && (
                <span>
                  Deploy your Decoy Beacon before starting the Core’s turn.
                </span>
              )}
            </div>
          )}
          <small className="coreboundNextAttack">
            FOLLOWING ROUND: {nextIntent.name} · {nextIntent.faces.join(' · ')}{' '}
            HP faces
          </small>
          {intent.enraged && (
            <small className="coreboundCorePhaseNotice">
              Meltdown: +2 included in each face.
            </small>
          )}
          <RelicAbilityBar
            game={game}
            context="warning"
            onToggleDecoyBeacon={() => {
              if (!getDecoyBeaconState(game).canDeploy) return;
              playSound('/sounds/deploy-beacon.mp3');
              setGame(toggleDecoyBeacon);
            }}
          />
          <div className="coreboundBossWarningActions">
            <button
              className="coreboundBossWarningSkip"
              type="button"
              aria-label="Start the Core's turn"
              onClick={finishBossWarning}
            >
              <span className="coreboundAttackStartIcon" aria-hidden="true">
                ϟ
              </span>
              <span>
                FACE THE CORE<small>Begin counterattack</small>
              </span>
              <span className="coreboundAttackStartArrow" aria-hidden="true">
                <CoreboundIcon name="relay" />
              </span>
            </button>
          </div>
        </div>
      )}
      <HealingFeedback
        game={game}
        maxHp={shownMaxHp}
        reduceMotion={reduceMotion}
        durationMs={650}
        suspended={
          stage !== 'playerOutcome' || phaseSpellPending || protocolPending
        }
      />
      {laserActive && (
        <CoreCounterattack
          showHealth={false}
          stage={stage}
          corePhase={corePhase}
          outcome={laserOutcome}
          damage={incomingDamage}
          attackType={counterAttack.attackType}
          baseDamage={counterAttack.baseDamage}
          protocolSaved={counterAttack.protocolSaved}
          decoyDamage={counterAttack.decoyDamage}
          absorbed={counterAttack.reductions.reduce(
            (total, reduction) => total + reduction.amount,
            0
          )}
          hpBefore={hpBeforeImpact}
          hpAfter={hpAfterImpact}
          hpCurrent={game.hp}
          maxHp={shownMaxHp}
          maxHpGained={roundHealth.maxHpGained}
          healed={healed}
          reduceMotion={reduceMotion}
          fallback={!coreWebglAvailable}
        />
      )}
      {!result && (
        <div className="coreboundIntent">
          <span className="coreboundPanelKicker">
            INCOMING:{' '}
            {massiveCoreAttack ? 'MASSIVE ATTACK' : intent.name.toUpperCase()}
          </span>
          <p>
            {getCoreCounterplay(intent.name)} Damage faces:{' '}
            <CoreDamagePreview intent={intent} />.
            {massiveCoreAttack
              ? ` MASSIVE ATTACK adds ${CORE_MASSIVE_ATTACK_BONUS} HP damage.`
              : ''}
            {intent.enraged ? ' Meltdown: +2 included in each face.' : ''}
          </p>
          <small className="coreboundNextAttack">
            FOLLOWING ROUND: {nextIntent.name} · {nextIntent.faces.join(' · ')}{' '}
            HP faces. Meltdown may strengthen these.
          </small>
          {!!game.coreShield && (
            <p className="coreboundHint">
              Core shield: blocks up to {game.coreShield} damage from your next
              attack.
            </p>
          )}
        </div>
      )}

      {ready && (
        <p className="coreboundHint coreboundBossHint">
          Your attack style changes each face before the throw. The stopped
          board value becomes direct damage, followed by relic bonuses and any
          Core shield. No second style multiplier is applied. The Core evolves
          at 800, 600, 400, and 200 HP, corrupting beneficial tiles and
          upgrading its own board. Corrupted tiles subtract 100 from your roll
          and remain for the fight. Emergence and Evolution each corrupt a
          different row's beneficial tiles. Pulling the sling controls direction
          and launch power. POWER UP tiles strengthen each face and relaunch the
          die. Each activation raises that tile by one level: +20% at ×1, +40%
          at ×2, up to +200% at ×10.
        </p>
      )}

      {['bossPlayerRolled', 'bossRolled'].includes(game.phase) && (
        <>
          {stage === 'waiting' && recoveryPending ? (
            <>
              <p>Your battle rolls are locked. Resume the reveal.</p>
              <SparkButton
                onClick={() => {
                  playSound('/sounds/whoosh-cinematic-sound.mp3');
                  setPlayerImpactComplete(false);
                  setRecoveryPending(false);
                  setStage(getBossResumeStage(game));
                }}
              >
                Reveal battle rolls →
              </SparkButton>
            </>
          ) : (
            <>
              {stage === 'playerImpact' && (
                <>
                  {attack.effects.length > 0 && (
                    <div className="coreboundRelicFeedbackStack" role="status">
                      {attack.effects.map((effect) => (
                        <RelicActivation
                          key={effect.id}
                          activation={effect}
                          label="DAMAGE BOOST"
                          reduceMotion={reduceMotion}
                        />
                      ))}
                    </div>
                  )}
                  {game.flipped && (
                    <RelicActivation
                      activation={{
                        id: 'mirrorChip',
                        message: `${formatRoll(
                          game.roll
                        )} reversed to ${formatRoll(getBaseRoll(game))} · ${
                          attack.damage
                        } total damage`,
                      }}
                      reduceMotion={reduceMotion}
                    />
                  )}
                  <p className="coreboundBossAutoRetaliation" role="status">
                    {lethal
                      ? 'You defeated the Core!'
                      : 'Your attack landed. Use a relic, then start the Core’s turn when you are ready.'}
                  </p>
                </>
              )}
              {stage === 'bossOutcome' && (
                <p className="coreboundBossAutoRetaliation" role="status">
                  Reading counter-damage…
                </p>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default BossBattle;
